import type { AguiEvent } from '@/types/agui';

/**
 * Renders a structured `run_error` event into the markdown body of an
 * assistant chat message. The summary line plus an expandable details block
 * (when present) keeps the chat clean while still giving the user the full
 * context of what failed and why.
 *
 * Falls back to the previous simple "❌ {message}" rendering when no
 * structured payload is present.
 *
 * Extracted out of `agentChatAguiStore.ts` so unit tests can exercise the
 * real implementation rather than re-implementing it. The contract here is
 * the **user-visible** failure card text — keep it stable and only modify
 * with intention.
 */
export function formatStructuredErrorMessage(userVisibleError: string, event: AguiEvent): string {
  const parts: string[] = [`❌ ${userVisibleError}`];

  const label = reasonLabel(event.reason);
  if (label) {
    parts.push(`\n_${label}_`);
  }

  if (event.last_errors && event.last_errors.length > 0) {
    parts.push('\n**What blocked completion:**');
    const shown = event.last_errors.slice(0, 6);
    for (const err of shown) {
      parts.push(`- ${err}`);
    }
    if (event.last_errors.length > shown.length) {
      parts.push(`- _… and ${event.last_errors.length - shown.length} more_`);
    }
  }

  if (event.last_known_good_workflow_json) {
    // Just hint at availability — the full "apply anyway" UI lives on the
    // proposal card, which was already emitted if a best-effort build
    // exists. Avoids dumping raw JSON into the chat text.
    parts.push('\n_A best-effort proposal from an earlier attempt is available — review it above before applying._');
  }

  return parts.join('\n');
}

/** Maps the wire-token reason to a human-readable phrase. */
function reasonLabel(reason: string | undefined): string | null {
  switch (reason) {
    case 'build_failed':            return 'Builder produced no usable output.';
    case 'plan_failed':             return 'Planner could not structure a plan from the request.';
    case 'validation_exhausted':    return 'Validation issues remained after every retry attempt.';
    case 'validation_inconclusive': return 'The validator could not verify the proposal.';
    case 'out_of_scope':            return "Request was outside the assistant's scope.";
    case 'provider_unavailable':    return 'The underlying AI provider was unreachable.';
    case 'internal':                return 'Internal orchestrator error.';
    default:                         return null;
  }
}
